import { UserRole, UserWithRoleDTO } from './dtos';

// Query parameters for user management
export interface UserQueryParams {
  role?: UserRole;
  tenantId?: string;
  domainId?: string;
  subjectId?: string;
  status?: 'invited' | 'active' | 'inactive';
  email?: string;
  name?: string;
  includeExpiredRoles?: boolean;
  includeUsersWithoutRoles?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'email' | 'createdAt' | 'role' | 'assignedAt';
  sortOrder?: 'asc' | 'desc';
}

// Sort options
export interface SortOption {
  value: 'name' | 'email' | 'createdAt' | 'role' | 'assignedAt';
  label: string;
}

export interface SortConfig {
  sortBy: 'name' | 'email' | 'createdAt' | 'role' | 'assignedAt';
  sortOrder: 'asc' | 'desc';
}

// Filter options
export interface FilterOption {
  value: string;
  label: string;
}

export interface RoleFilterOption extends FilterOption {
  value: UserRole;
}

export interface StatusFilterOption extends FilterOption {
  value: 'invited' | 'active' | 'inactive';
}

// User table data (for display purposes)
export interface UserTableData {
  id: string;
  name: string;
  email: string;
  status: 'invited' | 'active' | 'inactive';
  tenantId: string;
  roles: UserRoleTableData[];
  createdAt: string;
  updatedAt?: string;
}

export interface UserRoleTableData {
  userRoleId: string;
  role: UserRole;
  roleLabel: string;
  domainId?: string;
  subjectId?: string;
  assignedAt: string;
  assignedBy: string;
  expiresAt?: string;
  isExpired: boolean;
}

// Pagination info
export interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
  nextToken?: string;
}

// User management hook options
export interface UseUsersWithRolesOptions {
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  retry?: number | boolean;
  keepPreviousData?: boolean;
}

export interface UseUserWithRoleOptions {
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  retry?: number | boolean;
}

// User management search and filter state
export interface UserManagementFilters {
  role?: UserRole;
  status?: 'invited' | 'active' | 'inactive';
  tenantId?: string;
  domainId?: string;
  subjectId?: string;
  searchQuery?: string;
  includeExpiredRoles?: boolean;
  includeUsersWithoutRoles?: boolean;
}

// Hook return types
export interface UsersWithRolesResult {
  data: UserWithRoleDTO[];
  totalCount: number;
  pagination: PaginationInfo;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  isFetching: boolean;
}

export interface UserWithRoleResult {
  data: UserWithRoleDTO | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  isFetching: boolean;
}

// Role label mapping
export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.SYSTEM_ADMIN]: 'System Administrator',
  [UserRole.UNIVERSITY_ADMIN]: 'University Administrator',
  [UserRole.FACULTY_ADMIN]: 'Institute Administrator',
  [UserRole.PROFESSOR]: 'Lecturer',
  [UserRole.COURSE_REPRESENTATIVE]: 'Course Representative',
};

// Status label mapping
export const STATUS_LABELS: Record<'invited' | 'active' | 'inactive', string> = {
  invited: 'Invited',
  active: 'Active',
  inactive: 'Inactive',
};

// Default sort options
export const DEFAULT_SORT_OPTIONS: SortOption[] = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'createdAt', label: 'Created Date' },
  { value: 'role', label: 'Role' },
  { value: 'assignedAt', label: 'Role Assigned Date' },
];

// Default pagination settings
export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
